import {
	Box,
	Button,
	Icons,
	Table,
	Heading,
	Stack,
	Text,
	Component,
	TableDataCell,
	Inline,
	Select,
	joinAttributes,
	AsyncSelect,
	Dropdown,
	SwitchInput,
} from "@sembark-travel/ui/base"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui/dialog"
import { useEffect, useMemo, useRef, useState, useCallback } from "react"
import AddContactForm from "./AddContactForm"
import { Email } from "./Email"
import { PhoneNumber } from "./PhoneNumber"
import { IContact, IPhoneNumber } from "./store"
import { UpdateContactInDialog } from "./UpdateContact"
import { ISimpleListResponse, useXHR } from "@sembark-travel/xhr"

export function ContactsList({
	contacts,
	onCreate,
	onChange,
	phoneNumberRequired,
	emailRequired,
	readOnly,
}: {
	contacts?: Array<IContact>
	phoneNumberRequired?: boolean
	emailRequired?: boolean
	onChange?: () => void
	onCreate: (data: {
		name: string
		email?: string
		phone_numbers?: Array<{ country_code: string; number: string }>
	}) => Promise<IContact>
	readOnly?: boolean
}) {
	const list = useMemo(() => contacts || [], [contacts])
	const havePrimary = useMemo(() => list.some((c) => c.is_primary), [list])
	const [isOpen, show, close] = useDialog()
	const [showOnlyDisabled, setShowOnlyDisabled] = useState(false)

	useEffect(() => {
		const hasDisabled = list.some((c) => c.deleted_at !== null)
		if (hasDisabled === false) setShowOnlyDisabled(false)
	}, [list])

	const filteredContacts = useMemo(() => {
		if (showOnlyDisabled) {
			// Disabled: deleted_at is a non-null
			return list.filter((contact) => !!contact.deleted_at)
		}
		// Active: deleted_at is null
		return list.filter((contact) => !contact.deleted_at)
	}, [list, showOnlyDisabled])

	const haveMoreThanOne = (filteredContacts || []).length > 1

	const reorderPhoneNumbers = useCallback(
		(phone_numbers?: Array<IPhoneNumber<IContact>>) => {
			if (!Array.isArray(phone_numbers)) return phone_numbers
			const primary = phone_numbers.find((p) => p.is_primary)
			if (!primary) return phone_numbers
			return [primary, ...phone_numbers.filter((p) => p !== primary)]
		},
		[]
	)
	return (
		<Stack gap="4">
			<Inline gap="2" justifyContent="between">
				<Inline gap="2">
					<Box>
						<Icons.Users color="muted" size="4" />
					</Box>
					<Heading as="h4" fontSize="md">
						Contacts
					</Heading>
				</Inline>
				{list.length > 0 &&
				list.some((contact) => contact.deleted_at !== null) ? (
					<Inline gap="2" alignItems="center" as="label">
						<SwitchInput
							checked={showOnlyDisabled}
							onChange={(checked) => setShowOnlyDisabled(!!checked)}
						/>
						<Text>Only Disabled</Text>
					</Inline>
				) : null}
			</Inline>
			{(filteredContacts || []).length ? (
				<Table
					headers={
						!showOnlyDisabled && havePrimary
							? ["", "Name", "Phone Number", "Email", ""]
							: ["Name", "Phone Number", "Email", ""]
					}
					hover
					bordered
					responsive
					alignCols={{ [!showOnlyDisabled && havePrimary ? 4 : 3]: "right" }}
					rows={(filteredContacts || []).map((contact: IContact) => {
						const baseCells: Array<React.ReactNode> = [
							<Stack gap="1">
								<Text>{contact.name}</Text>
								<Text fontSize="sm" color="muted">
									{contact.designation || ""}
								</Text>
							</Stack>,
							<PhoneNumber
								numberOnly
								value={reorderPhoneNumbers(contact.phone_numbers)}
							/>,
							<Email emailOnly value={contact.email} />,
							readOnly ? null : (
								<Component initialState={false}>
									{({ state, setState }) => (
										<>
											<Dropdown alignRight>
												<Dropdown.ToggleButton size="sm" level="tertiary">
													<Icons.DotsVertical />
												</Dropdown.ToggleButton>
												<Dropdown.Menu>
													{contact.deleted_at !== null ? null : (
														<Dropdown.MenuItem onClick={() => setState(true)}>
															<Icons.Pencil /> Edit Contact
														</Dropdown.MenuItem>
													)}
													{contact.is_primary ||
													(filteredContacts.length === 1 &&
														!contact.deleted_at) ? null : (
														<DisableContact
															contact={contact}
															onSuccess={() => {
																setState(false)
																onChange?.()
															}}
														>
															{({ onToggleStatus, isProcessing }) => (
																<Dropdown.MenuItem
																	onClick={onToggleStatus}
																	color={
																		contact.deleted_at !== null
																			? "success"
																			: "warning"
																	}
																>
																	{isProcessing ? (
																		<>
																			<Icons.Refresh spin /> Processing...
																		</>
																	) : contact.deleted_at !== null ? (
																		<>
																			<Icons.Refresh /> Enable Contact{""}
																			<Icons.NewFeature width="10" height="6" />
																		</>
																	) : (
																		<>
																			<Icons.Ban /> Disable Contact{" "}
																			<Icons.NewFeature width="10" height="6" />
																		</>
																	)}
																</Dropdown.MenuItem>
															)}
														</DisableContact>
													)}
												</Dropdown.Menu>
											</Dropdown>
											<UpdateContactInDialog
												open={state}
												contact={contact}
												onCancel={() => setState(false)}
												phoneNumberRequired={phoneNumberRequired}
												emailRequired={emailRequired}
												onSuccess={() => {
													setState(false)
													showSnackbar("Contact details updated.")
													onChange?.()
												}}
												showPrimaryCheckbox={haveMoreThanOne}
											/>
										</>
									)}
								</Component>
							),
						]

						// Add flag cell only if `showOnlyDisabled` is false
						if (!showOnlyDisabled && havePrimary) {
							baseCells.unshift(
								contact.is_primary ? (
									<Inline
										padding="2"
										borderWidth="1"
										borderColor="primary"
										rounded="md"
										width="8"
									>
										<Icons.FlagSolid color="primary" title="Primary Contact" />
									</Inline>
								) : null
							)
						}

						return baseCells
					})}
				>
					{showOnlyDisabled || readOnly ? null : (
						<tfoot>
							<tr>
								<TableDataCell colSpan={havePrimary ? 5 : 4}>
									<Button data-testid="add_contact" onClick={show} size="sm">
										<Icons.Plus /> Add Contact
									</Button>
								</TableDataCell>
							</tr>
						</tfoot>
					)}
				</Table>
			) : (
				<Stack gap="2">
					<Text fontWeight="semibold" color="muted" fontSize="md">
						No contacts found
					</Text>
					<Text color="muted" fontSize="sm">
						Add contacts like spokesperson for better communication and sharing
						across organisation
					</Text>
					<Box>
						<Button
							onClick={show}
							data-testid="add_contact"
							status="primary"
							size="sm"
						>
							<Icons.Plus /> Add Contact
						</Button>
					</Box>
				</Stack>
			)}
			<Dialog open={isOpen} onClose={close} title="Add Contact">
				<Dialog.Body>
					<AddContactForm
						onCancel={close}
						phoneNumberRequired={phoneNumberRequired}
						emailRequired={emailRequired}
						onCreate={async (data) => {
							const contact = await onCreate(data)
							showSnackbar("Contact added.")
							return contact
						}}
						showPrimaryCheckbox={list.length > 0}
					/>
				</Dialog.Body>
			</Dialog>
		</Stack>
	)
}

function DisableContact({
	contact,
	onSuccess,
	children,
}: {
	contact: IContact
	onSuccess: () => void
	children: (props: {
		onToggleStatus: () => void
		isProcessing: boolean
	}) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isProcessing, setIsProcessing] = useState(false)
	const isDisabled = !!contact.deleted_at
	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					{children({
						isProcessing,
						onToggleStatus: async () => {
							if (isProcessing) return
							if (await confirm()) {
								setIsProcessing(true)
								try {
									const endpoint = `contacts/${contact.id}${isDisabled ? "/restore" : ""}`
									const response = await (isDisabled
										? xhr.patch(endpoint)
										: xhr.delete(endpoint))

									const message =
										response.data?.message ||
										`${contact.name} has been ${isDisabled ? "enabled" : "disabled"} successfully.`

									showSnackbar(message)
									onSuccess?.()
								} catch (e) {
									const error = e as Error
									alert(
										error.message || "Something went wrong. Please try again."
									)
								} finally {
									setIsProcessing(false)
								}
							}
						},
					})}
					<ConfirmationDialog
						title={
							isDisabled ? "Enable Contact Details" : "Disable Contact Details"
						}
						destructiveAction={!isDisabled}
						acceptActionLabel={
							isDisabled ? "Yes, Enable It" : "I understand, Disable It"
						}
					>
						<Stack gap="2">
							{isDisabled ? (
								<Text>
									Are you sure you want to enable this contact details?
								</Text>
							) : (
								<Text>
									Are you sure you want to disable this contact details?
								</Text>
							)}
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

function AddContactDialog({
	isOpen,
	onClose,
	onCreate,
	...props
}: { isOpen: boolean; onClose: () => void } & Omit<
	React.ComponentProps<typeof AddContactForm>,
	"onCancel"
>) {
	return (
		<Dialog open={isOpen} onClose={onClose} title="Add Contact">
			<Dialog.Body>
				<AddContactForm
					onCancel={onClose}
					onCreate={async (data) => {
						const contact = await onCreate(data)
						showSnackbar("Contact added.")
						return contact
					}}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

export function SelectContact({
	onCreate,
	phoneNumberRequired,
	emailRequired,
	...props
}: Pick<
	React.ComponentProps<typeof AddContactDialog>,
	"onCreate" | "phoneNumberRequired" | "emailRequired"
> &
	React.ComponentProps<typeof Select>) {
	const [newName, setNewName] = useState("")
	return (
		<>
			<Select
				{...props}
				onCreateNew={(query: string) => {
					setNewName(query)
				}}
				optionRenderer={({ option, created }) => {
					if (created) {
						return <Text>Add "{option.name}"</Text>
					}
					return (
						<Stack gap="1">
							<Text>{option.name}</Text>
							<Text fontSize="sm" color="muted">
								{joinAttributes(option.phone_number, option.email)}
							</Text>
						</Stack>
					)
				}}
			/>
			<AddContactDialog
				isOpen={!!newName}
				initialValues={{
					name: newName,
				}}
				onClose={() => {
					setNewName("")
				}}
				onCreate={onCreate}
				phoneNumberRequired={phoneNumberRequired}
				emailRequired={emailRequired}
			/>
		</>
	)
}

export function SelectContactForContactables({
	onCreate,
	phoneNumberRequired,
	emailRequired,
	contactables,
	...props
}: Pick<
	React.ComponentProps<typeof AddContactDialog>,
	"onCreate" | "phoneNumberRequired" | "emailRequired"
> &
	Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
		contactables: {
			hotels?: Array<number>
		}
	}) {
	const [newName, setNewName] = useState("")
	const xhr = useXHR()
	const onSuccessRef = useRef<((value: unknown) => void) | undefined>(undefined)
	return (
		<>
			<AsyncSelect
				fetch={(q, { page }) =>
					xhr
						.get<ISimpleListResponse<IContact>>("/contacts", {
							params: {
								q,
								limit: 30,
								page,
								contactable_types: [contactables.hotels ? "hotels" : undefined],
								contactable_ids: { hotels: contactables.hotels },
							},
						})
						.then((resp) => resp.data.data)
				}
				{...props}
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						onSuccessRef.current = resolve
					})
				}}
				optionRenderer={({ option, created }) => {
					if (created) {
						return <Text>Add "{option.name}"</Text>
					}
					return (
						<Stack gap="1">
							<Text>{option.name}</Text>
							<Text fontSize="sm" color="muted">
								{joinAttributes(option.phone_number, option.email)}
							</Text>
						</Stack>
					)
				}}
			/>
			<AddContactDialog
				isOpen={!!newName}
				initialValues={{
					name: newName,
				}}
				onClose={() => {
					setNewName("")
				}}
				onCreate={async (...args) => {
					const contact = await onCreate(...args)
					onSuccessRef.current?.(contact)
					onSuccessRef.current = undefined
					return contact
				}}
				phoneNumberRequired={phoneNumberRequired}
				emailRequired={emailRequired}
			/>
		</>
	)
}
